import React, { useContext, useState } from 'react';
import styled from 'styled-components/macro';
import { userHasPermission } from '../../../authorisation/permission';
import { ReactComponent as ChevronRight } from '../../../images/icons/chevron-right.svg';
import { Expandable } from '../../../shared/Expandable';
import { Header3 } from '../../../shared/Headers';
import { Table, TBody, Th, THead, Tr } from '../../../shared/Table';
import { spacing02, spacing03, spacing04, spacing05, spacing06 } from '../../../styling/spacing';
import { UserContext } from '../../../users/UserContext';
import { CustomerQuotesResponse } from '../../projects/projectQuotesAndDesignsResponse';
import { CustomerQuoteRow } from './CustomerQuoteRow';

type Props = {
  projectId: number;
  customerQuotesResponse: CustomerQuotesResponse;
};

export const CustomerQuotes = ({ projectId, customerQuotesResponse }: Props) => {
  const { customerId, customerReference, name, quotes } = customerQuotesResponse;
  const [isExpanded, setIsExpanded] = useState(true);

  const userCanEditQuotes = userHasPermission({ user: useContext(UserContext).getUser(), permission: 'Edit' });

  return (
    <CustomerContainer key={customerId} $isExpanded={isExpanded}>
      <CustomerHeader $isExpanded={isExpanded} onClick={() => setIsExpanded(!isExpanded)}>
        <ChevronWrapper $isExpanded={isExpanded}>
          <Chevron />
        </ChevronWrapper>
        <CustomerName>{`${customerReference} ${name}`}</CustomerName>
      </CustomerHeader>
      <Expandable isExpanded={isExpanded}>
        <CustomerQuotesTable>
          <THead>
            <Tr>
              <NameHeaderCell>Quote Title</NameHeaderCell>
              <ComponentsHeaderCell>Components</ComponentsHeaderCell>
              <QuoteReferencesHeaderCell colSpan={3}>NetSuite Quote Reference(s)</QuoteReferencesHeaderCell>
              <DateHeaderCell>Created</DateHeaderCell>
              <CreatedByHeaderCell>Created By</CreatedByHeaderCell>
              <DateHeaderCell>Uploaded</DateHeaderCell>
              <ActionsHeaderCell />
            </Tr>
          </THead>
          <TBody>
            {quotes.map(quote => (
              <CustomerQuoteRow
                key={quote.quoteId}
                projectId={projectId}
                quote={quote}
                userCanEditQuotes={userCanEditQuotes}
              />
            ))}
          </TBody>
        </CustomerQuotesTable>
      </Expandable>
    </CustomerContainer>
  );
};

const CustomerContainer = styled.div<{ $isExpanded: boolean }>`
  margin-bottom: ${props => (props.$isExpanded ? spacing06 : spacing03)};
`;

const CustomerQuotesTable = styled(Table)`
  width: 100%;
  table-layout: fixed;
`;

const NameHeaderCell = styled(Th)`
  width: 60%;
`;

const ComponentsHeaderCell = styled(Th)`
  width: 100px;
`;

const QuoteReferencesHeaderCell = styled(Th)`
  width: 310px;
`;

const CreatedByHeaderCell = styled(Th)`
  width: 40%;
`;

const DateHeaderCell = styled(Th)`
  width: 125px;
`;

const ActionsHeaderCell = styled(Th)`
  width: 60px;
`;

const CustomerHeader = styled(Header3)<{ $isExpanded: boolean }>`
  display: inline-flex;
  align-items: center;
  margin-bottom: ${spacing04};
  user-select: none;
  cursor: pointer;
  transition: all 0.25s ease;
  opacity: ${props => (props.$isExpanded ? 1 : 0.8)};

  &:hover {
    opacity: 0.7;
  }
`;

const ChevronWrapper = styled.div<{ $isExpanded: boolean }>`
  height: ${spacing05};
  width: ${spacing05};
  transition: all 0.25s ease;
  transform: rotate(${props => (props.$isExpanded ? '90deg' : '0deg')});
`;

const Chevron = styled(ChevronRight)`
  height: ${spacing05};
  width: ${spacing05};
`;

const CustomerName = styled.span`
  margin-left: ${spacing02};
`;
